import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { links } from '@eis/core';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { DetailsSection, usePermissions } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useLeadPremiumEstimate, usePolicyPremiumEstimate } from '../api/premium';
import {
  currencyOfPremium,
  engineStampOf,
  formatAmount,
  periodOf,
  taxModeOfPremium,
  totalsOfPremium,
} from '../model/format';
import { PremiumBreakdownTable } from './PremiumBreakdownTable';
import { PremiumEstimate, type EstimateState } from './PremiumEstimate';

/**
 * The *Prämie* section (SDD-021 §9): one place that says what a policy — or a lead's draft —
 * costs, and why.
 *
 *  - the premium owed per period ("8,59 € / Monat") and how its tax works (the tax mode);
 *  - *Tatsächlich* and *Geschätzt* side by side, with the difference and its reasons;
 *  - for a member, the note that group-level adjustments live on the group policy, with a link;
 *  - the breakdown, with the helpers on request;
 *  - the engine stamp — what priced it, from what — in a details tooltip.
 *
 * Presentational: the estimate comes in as a state, so the section renders the same whether the
 * policy page, the lead page or a story feeds it. `PolicyPremiumSection` and
 * `LeadPremiumSection` below are the two that ask the service.
 */
export function PremiumSection({
  premium,
  estimate,
  groupPolicyCode,
  locale,
}: {
  readonly premium: InstanceApi.Premium | undefined;
  readonly estimate: EstimateState;
  /** A member's group policy: the note and its link. */
  readonly groupPolicyCode?: string;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const { can } = usePermissions();
  const totals = totalsOfPremium(premium);
  const currency = currencyOfPremium(premium);
  const period = periodOf(premium?.period);
  const taxMode = taxModeOfPremium(premium);
  const stamp = engineStampOf(premium?.engine);
  const parent = groupPolicyCode ?? '';

  return (
    <DetailsSection
      title={t('premium.title')}
      description={t('premium.description')}
      actions={
        stamp === '' ? undefined : (
          <Tooltip
            title={
              <Stack spacing={0.25}>
                <span>{t('premium.engine.stamp', { engine: stamp })}</span>
                {(premium?.engine?.compiler ?? '') !== '' && (
                  <span>
                    {t('premium.engine.compiler', { compiler: premium?.engine?.compiler ?? '' })}
                  </span>
                )}
                {(premium?.inputDigest ?? '') !== '' && (
                  <span>{t('premium.engine.digest', { digest: premium?.inputDigest ?? '' })}</span>
                )}
              </Stack>
            }
          >
            <IconButton
              size="small"
              aria-label={t('premium.engine.label')}
              data-testid="engine-stamp"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
      fields={[
        {
          label: t('premium.amount'),
          // The gross per period, with the period it is owed for (SDD-015 B-6, Q-3).
          value:
            totals.gross === undefined
              ? undefined
              : `${formatAmount(totals.gross, currency, locale)} ${t(`policies.premiumPeriod.${period}`)}`,
        },
        ...(taxMode === undefined
          ? []
          : [{ label: t('premium.taxModeLabel'), value: t(`premium.taxMode.${taxMode}`) }]),
      ]}
    >
      <Stack spacing={2.5}>
        <PremiumEstimate premium={premium} estimate={estimate} locale={locale} />

        {parent !== '' && (
          <Alert severity="info" data-testid="member-group-note">
            <Typography variant="body2">{t('premium.member.note')}</Typography>
            {can(p.groupPolicies.view) && (
              <Link
                href={links.insurance.groupPolicy(parent)}
                onClick={(event) => {
                  event.preventDefault();
                  void navigate({ href: links.insurance.groupPolicy(parent) });
                }}
              >
                {t('premium.member.link', { code: parent })}
              </Link>
            )}
          </Alert>
        )}

        <PremiumBreakdownTable premium={premium} locale={locale} />
      </Stack>
    </DetailsSection>
  );
}

/** The estimate query as the section's state. */
function stateOf(query: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly data?: { readonly estimate?: InstanceApi.PremiumEstimate };
}): EstimateState {
  if (query.isError) return { status: 'failed' };
  if (query.isPending || query.data === undefined) return { status: 'loading' };
  return query.data.estimate === undefined
    ? { status: 'failed' }
    : { status: 'ready', estimate: query.data.estimate };
}

/**
 * The policy's *Prämie* (policy detail and member detail): the snapshot's premium, and the
 * estimate at the same business day, asked AFTER the page has rendered.
 */
export function PolicyPremiumSection({
  code,
  validAt,
  premium,
  groupPolicyCode,
  locale,
}: {
  readonly code: string;
  /** The page's business day; absent = today (the service's default). */
  readonly validAt?: string;
  readonly premium: InstanceApi.Premium | undefined;
  readonly groupPolicyCode?: string;
  readonly locale: string;
}): ReactNode {
  const estimate = usePolicyPremiumEstimate(code, validAt);
  return (
    <PremiumSection
      premium={premium}
      estimate={stateOf(estimate)}
      groupPolicyCode={groupPolicyCode}
      locale={locale}
    />
  );
}

/**
 * A lead's *Prämie*: *Tatsächlich* is the draft's priced premium, *Geschätzt* the refresh preview
 * — on a stale pin the refreshed version's price, reason *Version geändert* (U-4).
 */
export function LeadPremiumSection({
  code,
  premium,
  groupPolicyCode,
  locale,
}: {
  readonly code: string;
  readonly premium: InstanceApi.Premium | undefined;
  readonly groupPolicyCode?: string;
  readonly locale: string;
}): ReactNode {
  const estimate = useLeadPremiumEstimate(code);
  return (
    <PremiumSection
      premium={premium}
      estimate={stateOf(estimate)}
      groupPolicyCode={groupPolicyCode}
      locale={locale}
    />
  );
}
